import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import {
	type EmailTemplateRecord,
	getEmailTemplates,
	previewEmailTemplate,
	resetEmailTemplate,
	sendTestEmail,
	updateEmailTemplate,
} from "../../lib/api";
import { cn } from "../../lib/cn";
import styles from "./EmailTemplatesAdminPage.module.css";

/**
 * Email template management (/admin/email-templates), US-ETM-003/004.
 *
 * Lists every editable transactional template with its current text (an
 * administrator override when present, otherwise the built-in wording), and
 * provides a subject/body editor with placeholder help, a live text + HTML
 * preview rendered from sample data, save/reset, and a real test-send to the
 * admin's own address. The HTML wrapper stays code-owned (DEC-ETM-001): only
 * wording is editable.
 */

export function EmailTemplatesAdminPage() {
	const { canManageUsers } = useAuth();

	const [templates, setTemplates] = useState<EmailTemplateRecord[] | null>(
		null,
	);
	const [selectedKind, setSelectedKind] = useState<string | null>(null);
	const [subject, setSubject] = useState("");
	const [body, setBody] = useState("");
	const [testTo, setTestTo] = useState("");
	const [preview, setPreview] = useState<{
		subject: string;
		body: string;
		html: string;
	} | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);

	const subjectRef = useRef<HTMLInputElement>(null);
	const bodyRef = useRef<HTMLTextAreaElement>(null);
	const insertTargetRef = useRef<"subject" | "body">("body");

	const load = useCallback(async (): Promise<void> => {
		try {
			const result = await getEmailTemplates();
			setTemplates(result.templates);
		} catch (loadError) {
			setError(
				loadError instanceof Error
					? loadError.message
					: "Could not load templates",
			);
		}
	}, []);

	useEffect(() => {
		if (!canManageUsers) return;
		void load();
	}, [canManageUsers, load]);

	const selected =
		templates?.find((entry) => entry.kind === selectedKind) ?? null;

	const selectTemplate = (template: EmailTemplateRecord): void => {
		setSelectedKind(template.kind);
		setSubject(template.subject);
		setBody(template.body);
		setPreview(null);
		setNotice(null);
		setError(null);
	};

	const refreshSelected = (template: EmailTemplateRecord): void => {
		setTemplates((current) =>
			current
				? current.map((entry) =>
						entry.kind === template.kind ? template : entry,
					)
				: current,
		);
		setSelectedKind(template.kind);
		setSubject(template.subject);
		setBody(template.body);
	};

	const run = async (action: () => Promise<void>): Promise<void> => {
		if (busy) return;
		setBusy(true);
		setError(null);
		setNotice(null);
		try {
			await action();
		} catch (actionError) {
			setError(
				actionError instanceof Error ? actionError.message : "Action failed",
			);
		} finally {
			setBusy(false);
		}
	};

	const handleSave = (): void => {
		if (!selectedKind) return;
		void run(async () => {
			const result = await updateEmailTemplate(selectedKind, { subject, body });
			refreshSelected(result.template);
			setNotice(
				`Saved — "${result.template.label}" now overrides the built-in template.`,
			);
		});
	};

	const handleReset = (): void => {
		if (!selectedKind) return;
		void run(async () => {
			const result = await resetEmailTemplate(selectedKind);
			refreshSelected(result.template);
			setNotice(
				`Reset — "${result.template.label}" is back to the built-in wording.`,
			);
			setPreview(null);
		});
	};

	const handlePreview = (): void => {
		if (!selectedKind) return;
		void run(async () => {
			const rendered = await previewEmailTemplate(selectedKind, {
				subject,
				body,
			});
			setPreview(rendered);
		});
	};

	const handleSendTest = (): void => {
		if (!selectedKind) return;
		void run(async () => {
			const result = await sendTestEmail(selectedKind, {
				subject,
				body,
				to: testTo || undefined,
			});
			setNotice(
				result.delivered
					? `Test email sent to ${result.to}. It is recorded in the email log.`
					: "SMTP is not configured here — the test email was logged to the server console instead (dev fallback).",
			);
		});
	};

	/** Inserts a placeholder tag at the cursor of the last-focused field. */
	const insertPlaceholder = (tag: string): void => {
		const isSubject = insertTargetRef.current === "subject";
		const element = isSubject ? subjectRef.current : bodyRef.current;
		if (!element) return;
		const token = `{{${tag}}}`;
		const start = element.selectionStart ?? element.value.length;
		const end = element.selectionEnd ?? start;
		const next = `${element.value.slice(0, start)}${token}${element.value.slice(end)}`;
		if (isSubject) {
			setSubject(next);
		} else {
			setBody(next);
		}
		requestAnimationFrame(() => {
			element.focus();
			element.setSelectionRange(start + token.length, start + token.length);
		});
	};

	if (!canManageUsers) {
		return (
			<div className={styles.root}>
				<div className={styles.head}>
					<h1 className={styles.title}>Email templates</h1>
				</div>
				<p className={styles.deny} role="alert">
					You do not have permission to manage email templates.
				</p>
			</div>
		);
	}

	return (
		<div className={styles.root}>
			<div className={styles.head}>
				<div className={styles.headline}>
					<h1 className={styles.title}>Email templates</h1>
					<p className={styles.lede}>
						The wording of every transactional email the system sends. Edits
						apply to the next send without a deploy; the branded layout stays
						consistent. Use {"{{tags}}"} to place live values — a tag with no
						value renders empty.
					</p>
				</div>
			</div>

			{error && (
				<p className={styles.actionError} role="alert">
					{error}
				</p>
			)}
			{notice && (
				<p className={styles.notice} role="status">
					{notice}
				</p>
			)}

			{templates === null ? (
				<p className={styles.empty}>Loading templates…</p>
			) : (
				<>
					<div className={styles.tableFrame}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th scope="col">Template</th>
									<th scope="col">Status</th>
									<th scope="col">Last updated</th>
									<th scope="col">
										<span className={styles.visuallyHidden}>Actions</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{templates.map((template) => (
									<tr
										key={template.kind}
										className={cn(
											selectedKind === template.kind && styles.rowActive,
										)}
									>
										<td data-label="Template">
											<div className={styles.cellStack}>
												<span className={styles.cellPrimary}>
													{template.label}
												</span>
												<span className={styles.cellMeta}>
													{template.description}
												</span>
											</div>
										</td>
										<td data-label="Status">
											<span
												className={cn(
													styles.badge,
													template.overridden && styles.badgeOverride,
												)}
											>
												{template.overridden ? "Overridden" : "Built-in"}
											</span>
										</td>
										<td data-label="Last updated" className={styles.cellMeta}>
											{template.updatedAt
												? `${new Date(template.updatedAt).toLocaleString()}${
														template.updatedBy
															? ` by ${template.updatedBy}`
															: ""
													}`
												: "—"}
										</td>
										<td data-label="Actions">
											<button
												type="button"
												className={styles.rowButton}
												onClick={() => selectTemplate(template)}
											>
												Edit
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{selected && (
						<section
							className={styles.editor}
							aria-label={`Edit ${selected.label} template`}
						>
							<div className={styles.editorHead}>
								<h2 className={styles.editorTitle}>{selected.label}</h2>
								<p className={styles.editorMeta}>{selected.description}</p>
							</div>

							<div className={styles.field}>
								<label className={styles.label} htmlFor="template-subject">
									Subject
								</label>
								<input
									id="template-subject"
									ref={subjectRef}
									className={styles.input}
									value={subject}
									onFocus={() => {
										insertTargetRef.current = "subject";
									}}
									onChange={(event) => setSubject(event.currentTarget.value)}
								/>
							</div>

							<div className={styles.label}>
								Placeholder tags — click to insert at the cursor:
							</div>
							<div className={styles.chips}>
								{selected.placeholders.map((placeholder) => (
									<button
										key={placeholder.tag}
										type="button"
										className={styles.chip}
										title={placeholder.description}
										onClick={() => insertPlaceholder(placeholder.tag)}
									>
										{`{{${placeholder.tag}}}`}
									</button>
								))}
							</div>
							<p className={styles.hint}>
								Unknown tags are removed from the sent email; a tag with no
								value renders empty.
							</p>

							<div className={styles.editorBodyField}>
								<label className={styles.label} htmlFor="template-body">
									Body (plain text — the branded HTML wrapper is applied around
									it)
								</label>
								<textarea
									id="template-body"
									ref={bodyRef}
									className={styles.textarea}
									rows={10}
									value={body}
									onFocus={() => {
										insertTargetRef.current = "body";
									}}
									onChange={(event) => setBody(event.currentTarget.value)}
								/>
							</div>

							<div className={styles.editorActions}>
								<button
									type="button"
									className={styles.secondaryButton}
									onClick={handlePreview}
									disabled={busy}
								>
									Preview
								</button>
								<button
									type="button"
									className={styles.primaryButton}
									onClick={handleSave}
									disabled={busy}
								>
									Save
								</button>
								{selected.overridden && (
									<button
										type="button"
										className={styles.secondaryButton}
										onClick={handleReset}
										disabled={busy}
									>
										Reset to built-in
									</button>
								)}
							</div>

							<div className={styles.testRow}>
								<label className={styles.label} htmlFor="template-test-to">
									Send test to
								</label>
								<input
									id="template-test-to"
									className={styles.input}
									type="email"
									placeholder="Defaults to your own email address"
									value={testTo}
									onChange={(event) => setTestTo(event.currentTarget.value)}
								/>
								<button
									type="button"
									className={styles.secondaryButton}
									onClick={handleSendTest}
									disabled={busy}
								>
									Send test
								</button>
							</div>
							<p className={styles.hint}>
								The test send uses the text above (saved or not) with sample
								data, and is recorded in the email log.
							</p>

							{preview && (
								<div className={styles.preview}>
									<h3 className={styles.previewTitle}>Preview (sample data)</h3>
									<p className={styles.previewSubject}>
										<strong>Subject:</strong> {preview.subject}
									</p>
									<pre className={styles.previewText}>{preview.body}</pre>
									<iframe
										className={styles.previewHtml}
										title="HTML email preview"
										srcDoc={preview.html}
										sandbox=""
									/>
								</div>
							)}
						</section>
					)}
				</>
			)}
		</div>
	);
}
